/**
 * Minimal YouTube embed controller over postMessage.
 *
 * We do not use `new YT.Player(iframe)` for the hall TV: Google Analytics
 * enhanced measurement attaches its own YT.Player to every enablejsapi embed
 * as soon as the iframe is inserted. The embed completes the handshake with
 * whichever player asks first; the loser gets `alreadyInitialized`, never
 * receives onReady, and has no seekTo/getCurrentTime. Safari Private Browsing
 * blocks GA, which is why the TV only worked there.
 *
 * This bridge reads every message from our iframe regardless of which player
 * id it is addressed to, and sends commands directly, so another listener on
 * the same embed cannot disable it.
 */

export const YT_STATE = Object.freeze({
  UNSTARTED: -1,
  ENDED: 0,
  PLAYING: 1,
  PAUSED: 2,
  BUFFERING: 3,
  CUED: 5
});

const HANDSHAKE_INTERVAL_MS = 250;
const HANDSHAKE_MAX_MS = 60000;
const MAX_EXTRAPOLATE_MS = 5000;

let nextBridgeId = 900000;

function parseMessage(data) {
  if (data && typeof data === 'object') return data;
  if (typeof data !== 'string') return null;
  try {
    const parsed = JSON.parse(data);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_) {
    return null;
  }
}

export function attachYoutubeEmbed(iframe, { onReady, onStateChange, now = () => Date.now() } = {}) {
  const id = ++nextBridgeId;
  const info = {};
  let timeAtMs = 0;
  let ready = false;
  let destroyed = false;
  let lastState = null;
  let handshakeTimer = null;
  const startedAtMs = now();

  const post = (payload) => {
    const target = iframe?.contentWindow;
    if (!target) return false;
    try {
      target.postMessage(JSON.stringify({ ...payload, id, channel: 'widget' }), '*');
      return true;
    } catch (_) {
      return false;
    }
  };
  const command = (func, args = []) => post({ event: 'command', func, args });

  const emitState = (state) => {
    if (!Number.isFinite(state)) return;
    info.playerState = state;
    if (state === lastState) return;
    lastState = state;
    if (typeof onStateChange === 'function') onStateChange({ data: state, target: api });
  };

  const mergeInfo = (delta) => {
    if (!delta || typeof delta !== 'object') return;
    if (delta.currentTime != null) timeAtMs = now();
    const { playerState, ...rest } = delta;
    Object.assign(info, rest);
    if (playerState != null) emitState(Number(playerState));
  };

  const stopHandshake = () => {
    if (handshakeTimer) clearInterval(handshakeTimer);
    handshakeTimer = null;
  };

  const markReady = () => {
    if (ready) return;
    ready = true;
    stopHandshake();
    command('addEventListener', ['onStateChange']);
    if (typeof onReady === 'function') onReady({ target: api });
  };

  const handleMessage = (event) => {
    if (destroyed || !iframe || event.source !== iframe.contentWindow) return;
    const data = parseMessage(event.data);
    if (!data) return;
    const kind = data.event;
    if (kind === 'initialDelivery' || kind === 'infoDelivery') mergeInfo(data.info);
    if (kind === 'onStateChange') emitState(Number(data.info));
    if (
      kind === 'onReady'
      || kind === 'initialDelivery'
      || kind === 'alreadyInitialized'
      || kind === 'infoDelivery'
    ) {
      markReady();
    }
  };

  const sendListening = () => {
    if (destroyed || ready) return;
    if (now() - startedAtMs > HANDSHAKE_MAX_MS) {
      stopHandshake();
      return;
    }
    post({ event: 'listening' });
  };

  const handleLoad = () => {
    ready = false;
    sendListening();
    if (!handshakeTimer) handshakeTimer = setInterval(sendListening, HANDSHAKE_INTERVAL_MS);
  };

  const api = {
    isReady: () => ready,
    getCurrentTime: () => {
      const t = Number(info.currentTime);
      if (!Number.isFinite(t)) return 0;
      if (info.playerState !== YT_STATE.PLAYING || !timeAtMs) return t;
      const elapsedMs = Math.min(MAX_EXTRAPOLATE_MS, Math.max(0, now() - timeAtMs));
      const rate = Number(info.playbackRate) || 1;
      return t + (elapsedMs / 1000) * rate;
    },
    getReportedTime: () => {
      const t = Number(info.currentTime);
      return Number.isFinite(t) ? t : 0;
    },
    getDuration: () => {
      const d = Number(info.duration);
      return Number.isFinite(d) ? d : 0;
    },
    getPlayerState: () => (Number.isFinite(info.playerState) ? info.playerState : YT_STATE.UNSTARTED),
    getVideoData: () => info.videoData || {},
    get playerInfo() {
      return info;
    },
    seekTo: (seconds, allowSeekAhead = true) => {
      const t = Number(seconds);
      if (!Number.isFinite(t)) return;
      command('seekTo', [t, allowSeekAhead !== false]);
      info.currentTime = t;
      timeAtMs = now();
    },
    playVideo: () => command('playVideo'),
    pauseVideo: () => command('pauseVideo'),
    mute: () => {
      command('mute');
      info.muted = true;
    },
    unMute: () => {
      command('unMute');
      info.muted = false;
    },
    destroy: () => {
      destroyed = true;
      stopHandshake();
      window.removeEventListener('message', handleMessage);
      iframe?.removeEventListener?.('load', handleLoad);
    }
  };

  window.addEventListener('message', handleMessage);
  iframe?.addEventListener?.('load', handleLoad);
  sendListening();
  handshakeTimer = setInterval(sendListening, HANDSHAKE_INTERVAL_MS);

  return api;
}
